import * as React from "react";
import { createClasses } from "@bloomberg/media-ui-core";

import Byline from "~components/shared/byline/Byline";
import Headline from "~components/shared/headline/Headline";
import {
    type MediaConfig,
    Media,
    MediaFormat,
    sourceHasValidMedia,
} from "~components/shared/media/";
import { VariantBase } from "~components/shared/variant-base/VariantBase";
import {
    type MagazineSection,
    type VariationComponent,
    ZoneColumnCombo,
    isCuratedStory,
} from "~types/types";
import { integrityError } from "~utils/moduleIntegrity";

import integrityCheck from "./MagazinePageMagazineArchive.integrity";
import styles from "./MagazinePageMagazineArchive.module.css";

export interface MagazineIssueProps {
    sections: MagazineSection[];
}

export type MagazinePageMagazineArchiveProps = VariationComponent &
    MagazineIssueProps;

const MEDIA_PRIORITY: MediaFormat[] = [MediaFormat.defaultImage];

const MEDIA_CONFIG: MediaConfig = {
    aspectRatio: 3 / 2,
    className: styles.itemMedia,
    desktopMaxWidth: 195,
    mobileMaxWidth: 103,
    showCaption: false,
    hideVideoDuration: true,
};

const SUPPORTED_REGIONS: ZoneColumnCombo[] = [ZoneColumnCombo.leftContentWell];

const SectionArchive = ({
    section,
    renderingLocation,
}: {
    section: MagazineSection;
    renderingLocation: ZoneColumnCombo;
}) => {
    const containerClasses = createClasses(styles.itemContainer, {
        [styles.fullWidthItemContainer]:
            renderingLocation === ZoneColumnCombo.fullWidthContentWell,
    });

    return (
        <div className={styles.sectionContainer}>
            <div className={styles.mobileSection}>
                <div className={styles.itemSection}>
                    <p>{section.title}</p>
                </div>
            </div>
            {section.items.map((item, index) => {
                return !isCuratedStory(item) ? null : (
                    <span key={item.url}>
                        <div className={containerClasses}>
                            <div className={styles.desktopSection}>
                                <div className={styles.itemSection}>
                                    {index === 0 && <p>{section.title}</p>}
                                </div>
                            </div>

                            <div className={styles.itemTextContainer}>
                                <a className={styles.storyLink} href={item.url}>
                                    <Headline
                                        text={item.headline}
                                        url={item.url}
                                        className={styles.itemHeadline}
                                        isPhoenix
                                    />
                                </a>
                                {item.byline && <Byline byline={item.byline} />}
                            </div>
                            {sourceHasValidMedia(item, MEDIA_PRIORITY) ? (
                                <Media
                                    source={item}
                                    priority={MEDIA_PRIORITY}
                                    config={MEDIA_CONFIG}
                                />
                            ) : (
                                <div className={styles.itemMediaPlaceholder} />
                            )}
                        </div>
                        <hr className={styles.itemEndline} />
                    </span>
                );
            })}
        </div>
    );
};

export default function MagazinePageMagazineArchive(
    props: MagazinePageMagazineArchiveProps
) {
    const { renderingLocation, sections } = props;

    const sectionsWithItems = sections.filter(
        (section) => section.items.length
    );
    const integrity = integrityCheck(sectionsWithItems);

    if (integrity.length) return integrityError(integrity);

    return (
        <VariantBase
            source={props}
            className={styles.MagazinePageMagazineArchive}
            supportedRegions={SUPPORTED_REGIONS}
        >
            <div>
                {sectionsWithItems.map((section, i) => {
                    return (
                        <SectionArchive
                            key={i}
                            section={section}
                            renderingLocation={renderingLocation}
                        />
                    );
                })}
            </div>
        </VariantBase>
    );
}
